import React from "react";
import PageFrame from "./PageFrame";
import { Check, Minus } from "lucide-react";

function StatCard({ label, value }) {
    return (
        <div className="border border-hairline px-5 py-5 bg-white">
            <div className="eyebrow text-muted2" style={{ color: "#5C6B73" }}>{label}</div>
            <div className="serif-display text-2xl sm:text-3xl text-navy mt-2">{value}</div>
        </div>
    );
}

function NeighborhoodList({ items }) {
    return (
        <div className="border border-hairline divide-y divide-hairline bg-white">
            {items.map((n) => (
                <div key={n[0]} className="p-4">
                    <div className="flex items-baseline justify-between gap-3">
                        <div className="font-sans font-semibold text-navy text-sm">{n[0]}</div>
                        <div className="font-sans text-xs text-coral whitespace-nowrap">{n[1]}</div>
                    </div>
                    <div className="text-sm text-muted2 mt-1">{n[2]}</div>
                </div>
            ))}
        </div>
    );
}

function ProsConsList({ pros, cons }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 mt-16 border border-hairline">
            <div className="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-hairline">
                <div className="eyebrow text-navy mb-4" style={{ color: "#0A2533" }}>Pros</div>
                <ul className="space-y-3">
                    {pros.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-ink/90">
                            <Check size={16} className="text-navy mt-1 flex-shrink-0" />
                            <span>{p}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="p-6 sm:p-8 bg-sand/40">
                <div className="eyebrow mb-4">Cons</div>
                <ul className="space-y-3">
                    {cons.map((c) => (
                        <li key={c} className="flex items-start gap-3 text-ink/90">
                            <Minus size={16} className="text-coral mt-1 flex-shrink-0" />
                            <span>{c}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function Section({ title, body }) {
    return (
        <div>
            <h3 className="serif-display text-3xl text-navy mb-3">{title}</h3>
            <p className="text-ink/90 leading-relaxed text-lg">{body}</p>
        </div>
    );
}

export default function CityPage({ city, pageNum, totalPages }) {
    return (
        <PageFrame pageNum={pageNum} totalPages={totalPages} label={city.name}>
            <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
                    <div className="lg:col-span-7">
                        <div className="eyebrow">{city.subtitle}</div>
                        <h2 className="serif-display text-6xl sm:text-7xl text-navy mt-3">
                            {city.name}
                        </h2>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[16/10] overflow-hidden border border-hairline">
                            <img src={city.image} alt={city.name} className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-16">
                    <StatCard label="Median Home" value={city.stats.median_home} />
                    <StatCard label="Cost of Living" value={city.stats.cost_of_living} />
                    <StatCard label="Avg Commute" value={city.stats.avg_commute} />
                    <StatCard label="Population" value={city.stats.population} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7 space-y-10">
                        <Section title="Overview" body={city.overview} />
                        <Section title="Cost of Living" body={city.cost} />
                        <Section title="Schools & Healthcare" body={city.schools} />
                    </div>

                    <div className="lg:col-span-5 space-y-8">
                        <div>
                            <div className="eyebrow mb-3">Key Neighborhoods</div>
                            <NeighborhoodList items={city.neighborhoods} />
                        </div>

                        <div className="bg-sand pl-5 pr-5 py-5 border-l-[3px] border-coral">
                            <div className="eyebrow mb-2">Local Insider Tip</div>
                            <p className="font-serif italic text-ink/90 text-lg leading-relaxed">
                                {city.insider_tip}
                            </p>
                        </div>
                    </div>
                </div>

                <ProsConsList pros={city.pros} cons={city.cons} />
            </div>
        </PageFrame>
    );
}
